import { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase'; // Assuming firebase.js exports 'db'

/**
 * Custom React Hook to listen to a single Firestore document in real-time.
 *
 * @param {string} collectionName The name of the Firestore collection.
 * @param {string} documentId The ID of the document to listen to.
 * @returns {{data: object | null, loading: boolean, error: Error | null}}
 */
const useRealtimeDocument = (collectionName, documentId) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!collectionName || !documentId) {
      setError(new Error("Collection name and document ID must be provided."));
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const docRef = doc(db, collectionName, documentId);

    const unsubscribe = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        setData({ id: docSnap.id, ...docSnap.data() });
      } else {
        setData(null); // Document does not exist
      }
      setLoading(false);
    }, (err) => {
      console.error("Error fetching real-time document:", err);
      setError(err);
      setLoading(false);
    });

    // Cleanup function to unsubscribe from the listener when the component unmounts
    return () => unsubscribe();
  }, [collectionName, documentId]); // Re-run effect if collectionName or documentId changes

  return { data, loading, error };
};

export default useRealtimeDocument;
